/**
 * Catalogue and invocation types, mirroring `catalog.py` and `invoke.py`.
 *
 * The catalogue is generated from the target deployment's live OpenAPI schema, so these types
 * describe the *shape* of an operation, never a fixed list of operations.
 */

import { request } from "./http";

export type Danger = "none" | "warn" | "severe";

/** A JSON Schema fragment. Opaque here — it is handed to the form renderer as-is. */
export type JsonSchema = Record<string, unknown>;

export interface ParamSpec {
  name: string;
  location: "path" | "query";
  required: boolean;
  schema_: JsonSchema;
  description: string | null;
  /** A repeatable query parameter, e.g. usage's `group_by`. */
  explode: boolean;
}

/** A named, runnable set of inputs. Placeholders are already resolved when one is selected. */
export interface ExampleSpec {
  name: string;
  summary: string;
  note: string | null;
  path_params: Record<string, unknown>;
  query: Record<string, unknown>;
  body: unknown | null;
  extra_headers: Record<string, string>;
}

/** An example with its placeholders filled in from live data. */
export interface ResolvedExample extends ExampleSpec {
  /** Placeholders nothing could be found for, so the tester knows what to run first. */
  unresolved: string[];
  hints: string[];
}

export interface VerifySignal {
  field: string;
  means: string;
  expect: unknown;
  expect_truthy: boolean;
  expect_absent: boolean;
  when_wrong: string | null;
}

export interface VerifyCheck {
  how: string;
  operation: string | null;
  example: string | null;
  carry: Record<string, string>;
  command: string | null;
  wait: string | null;
}

export interface Verification {
  summary: string | null;
  signals: VerifySignal[];
  checks: VerifyCheck[];
}

export interface Operation {
  operation_id: string;
  method: string;
  path: string;
  group: string;
  task_label: string;
  summary: string | null;
  description: string | null;
  help_text: string | null;
  tags: string[];

  params: ParamSpec[];
  body_schema: JsonSchema | null;
  body_required: boolean;
  examples: ExampleSpec[];

  success_statuses: number[];
  /** 202 with a status endpoint the bench can poll on the tester's behalf. */
  returns_job: boolean;
  accepts_idempotency_key: boolean;
  required_profile: string | null;
  /** Identities that can actually run this. `system-user` is restrictive, not additive. */
  profiles: string[];
  /** Roles without which the service refuses the call. Almost always empty. */
  required_roles: string[];
  /** Whether a token carrying `system` may call this — true only for lifecycle. */
  system_allowed: boolean;
  project_required: boolean;
  danger: Danger;

  ui_order: string[];
  ui_hints: Record<string, unknown>;
  defaults: Record<string, unknown>;

  /** Takes a free-form JSON body despite declaring no schema (the SCM webhook). */
  raw_body: boolean;
  /** Headers the tester fills in, for calls authenticated outside the JWT flow. */
  extra_header_fields: string[];
  header_defaults: Record<string, string>;
  verify: Verification | null;
}

export interface Catalog {
  environment: string;
  api_title: string;
  api_version: string;
  operations: Operation[];
  groups: string[];
  warnings: string[];
}

export interface CatalogResponse {
  catalog: Catalog;
  /** The cached copy was served because the deployment could not be re-read. */
  stale: boolean;
}

export interface FieldError {
  field_path: string;
  message: string;
  kind: string | null;
}

export interface Problem {
  title: string;
  detail: string;
  status: number;
  request_id: string | null;
  field_errors: FieldError[];
  kind: "problem" | "validation" | "transport" | "unknown";
}

export interface JobHandle {
  job_id: string | null;
  poll_url: string | null;
  /** False for lifecycle and webhook 202s, which expose no status endpoint. */
  pollable: boolean;
}

export interface RequestPreview {
  method: string;
  url: string;
  headers: Record<string, string>;
  body: unknown | null;
}

export interface ResponsePreview {
  status: number;
  headers: Record<string, string>;
  body: unknown | null;
  text: string | null;
}

export interface InvocationResult {
  call_id: string;
  operation_id: string;
  task_label: string;
  method: string;
  url: string;
  status: number;
  ok: boolean;
  duration_ms: number;
  request_id: string | null;
  profile_used: string | null;
  idempotency_key: string | null;
  data: unknown | null;
  problem: Problem | null;
  job: JobHandle | null;
  renderer: string;
  curl: string;
  request: RequestPreview;
  response: ResponsePreview;
  started_at: string;
}

export interface InvocationInput {
  operation_id: string;
  path_params?: Record<string, string>;
  query?: Record<string, unknown>;
  body?: unknown;
  idempotency_key?: string;
  extra_headers?: Record<string, string>;
}

export function fetchCatalog(refresh = false, signal?: AbortSignal): Promise<CatalogResponse> {
  return request<CatalogResponse>(`/api/catalog${refresh ? "?refresh=true" : ""}`, {
    ...(signal ? { signal } : {}),
  });
}

export function invokeOperation(input: InvocationInput): Promise<InvocationResult> {
  return request<InvocationResult>("/api/invoke", { method: "POST", body: input });
}

export function resolveExample(
  operationId: string,
  exampleName: string,
): Promise<ResolvedExample> {
  return request<ResolvedExample>(
    `/api/operations/${encodeURIComponent(operationId)}/examples/${encodeURIComponent(exampleName)}`,
    { method: "POST" },
  );
}

/**
 * Whether a token carrying `roles` may call `operation`.
 *
 * Mirrors the service: a `system` token is confined to lifecycle, and an operation that names
 * roles gets none of them for free -- lifecycle demands `system`, a project reset demands
 * `memory-admin`. Anything else goes, whatever the token carries.
 *
 * The logic is here but the facts are not: `required_roles` and `system_allowed` come from the
 * server, so this cannot drift from the Python rule it mirrors.
 */
export function canRun(operation: Operation, roles: string[]): boolean {
  if (roles.includes("system") && !operation.system_allowed) {
    return false;
  }
  return operation.required_roles.every((role) => roles.includes(role));
}
